<template>
  <dashboard-block
    title="Когда заказывают"
    :caption="caption"
    :loading="loading"
    :missing="!block"
    :min-height="327"
    skeleton="image"
  >
    <div
      v-if="map"
      class="dashboard-hourly"
    >
      <div
        class="dashboard-hourly__grid"
        role="img"
        :aria-label="summary"
        :style="{ gridTemplateColumns: `2rem repeat(${map.hours.length}, minmax(0, 1fr))` }"
        @mouseleave="hovered = null"
      >
        <div></div>
        <div
          v-for="(hour, index) in map.hours"
          :key="`hour-${hour}`"
          class="dashboard-hourly__hour text-caption text--secondary"
        >
          {{ isHourLabelled(index) ? hour : '' }}
        </div>

        <template v-for="row in map.rows">
          <div
            :key="`day-${row.weekday}`"
            class="dashboard-hourly__day text-caption text--secondary"
          >
            {{ row.name }}
          </div>
          <div
            v-for="cell in row.cells"
            :key="`cell-${row.weekday}-${cell.hour}`"
            class="dashboard-hourly__cell"
            @mouseenter="hover(cell, $event)"
          >
            <div
              class="dashboard-hourly__fill"
              :style="{ opacity: opacity(cell.level) }"
            ></div>
          </div>
        </template>
      </div>

      <div class="d-flex align-center flex-wrap mt-3">
        <span class="text-body-2 text--primary me-4">{{ summary }}</span>
        <v-spacer></v-spacer>
        <span class="dashboard-hourly__legend text-caption text--secondary">
          <span class="me-2">меньше</span>
          <span
            v-for="level in legend"
            :key="level"
            class="dashboard-hourly__cell dashboard-hourly__cell--legend"
          >
            <span
              class="dashboard-hourly__fill"
              :style="{ opacity: opacity(level) }"
            ></span>
          </span>
          <span class="ms-2">больше</span>
        </span>
      </div>

      <v-tooltip
        top
        :value="Boolean(hovered)"
        :position-x="tooltip.x"
        :position-y="tooltip.y"
      >
        <span>{{ hovered ? hovered.tooltip : '' }}</span>
      </v-tooltip>
    </div>

    <div
      v-else
      class="text--secondary"
    >
      За эти недели заказов ещё не было — карта появится вместе с первыми заказами.
    </div>
  </dashboard-block>
</template>

<script>
import DashboardBlock from '@/views/dashboard/dashboard-block'
import { plural } from '@/helpers'
import { heatMap, heatSummary, toPeriodString } from '@/helpers/dashboard'

const MIN_OPACITY = 0.14

export default {
  name: 'DashboardHourly',
  components: { DashboardBlock },
  props: {
    block: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data: () => ({
    hovered: null,
    tooltip: { x: 0, y: 0 },
    legend: [0, 0.25, 0.5, 0.75, 1],
  }),
  computed: {
    map() {
      return this.block ? heatMap(this.block.cells) : null
    },
    summary() {
      return this.block ? heatSummary(this.block.cells) : ''
    },
    caption() {
      if (!this.block) return ''

      const { from, to, weeks } = this.block
      const period = toPeriodString(from, to)
      const span = weeks ? ` за ${weeks} ${plural(weeks, ['неделю', 'недели', 'недель'])}` : ''

      return `Среднее число заказов в час${span}${period ? `: ${period}` : ''}`
    },
  },
  methods: {
    opacity(level) {
      return level > 0 ? MIN_OPACITY + (1 - MIN_OPACITY) * level : 0
    },

    isHourLabelled(index) {
      return !this.$vuetify.breakpoint.xsOnly || index % 2 === 0
    },

    hover(cell, event) {
      const rect = event.currentTarget.getBoundingClientRect()

      this.hovered = cell
      this.$nextTick(() => {
        this.tooltip = { x: rect.left + rect.width / 2, y: rect.top }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-hourly {
  .dashboard-hourly__grid {
    display: grid;
    gap: 3px;
  }

  .dashboard-hourly__hour {
    text-align: center;
    line-height: 1.25rem;
  }

  .dashboard-hourly__day {
    display: flex;
    align-items: center;
  }

      .dashboard-hourly__cell {
    position: relative;
    height: 26px;
    border-radius: 4px;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: currentColor;
      opacity: 0.06;
    }
  }

  .dashboard-hourly__fill {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: var(--v-primary-base);
  }

  .dashboard-hourly__legend {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
  }

  .dashboard-hourly__cell--legend {
    display: inline-block;
    width: 16px;
    height: 16px;
    margin: 0 1px;
  }
}
</style>
